import React, { useState } from "react";
import { InView } from "react-intersection-observer";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const response = await fetch("https://car-rental-backend-wmlb.onrender.com/api/users/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccessMessage(result.message || "Your message has been sent successfully!");
        setFormData({ name: '', email: '', message: '' });
      } else {
        setErrorMessage(result.message || "Something went wrong, please try again.");
      }
    } catch (error) {
      setErrorMessage("Network error. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: "📞",
      title: "Call Us",
      details: "+91 96663 17749",
      description: "Mon-Sun, 6:00 AM - 11:00 PM",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: "✉️",
      title: "Email Us",
      details: "contact.vsdcars@gmail.com",
      description: "We'll respond within 24 hours",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      icon: "📍",
      title: "Visit Us",
      details: "Nupura Self Drive Cars",
      description: "Plot No 358, Teachers Colony, BN Reddy Nagar, Hayathnagar, Rangareddy, Telangana, India",
      gradient: "from-green-500 to-emerald-500"
    }
  ];

  return (
    <div className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-blue-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-80 h-80 bg-purple-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-blue-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <InView triggerOnce={true} threshold={0.3}>
          {({ inView, ref }) => (
            <div ref={ref} className={`text-center mb-16 ${inView ? 'animate__animated animate__fadeInDown' : ''}`}>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Get In <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Touch</span>
              </h2>
              <p className="text-xl text-blue-200 max-w-2xl mx-auto leading-relaxed">
                Ready to experience premium car rental services? We're here to help you with any questions or requirements.
              </p>
            </div>
          )}
        </InView>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Methods - Left Side */}
          <InView triggerOnce={true} threshold={0.3}>
            {({ inView, ref }) => (
              <div
                ref={ref}
                className={`lg:col-span-1 ${inView ? 'animate__animated animate__fadeInLeft' : ''}`}
              >
                <div className="space-y-6">
                  {contactMethods.map((method, index) => (
                    <div
                      key={index}
                      className="group bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:border-white/40 transition-all duration-500 hover:scale-105"
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`w-16 h-16 bg-gradient-to-r ${method.gradient} rounded-2xl flex items-center justify-center text-white text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                          {method.icon}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-bold text-lg mb-2">{method.title}</h3>
                          <p className="text-cyan-200 font-semibold text-sm mb-1">{method.details}</p>
                          <p className="text-blue-200 text-sm">{method.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Additional Info Card */}
                  <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 backdrop-blur-md rounded-2xl p-6 border border-cyan-400/30">
                    <h4 className="text-white font-bold text-lg mb-3">Quick Response Guaranteed</h4>
                    <ul className="text-blue-200 space-y-2 text-sm">
                      <li className="flex items-center space-x-2">
                        <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
                        <span>24/7 Customer Support</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
                        <span>Instant Booking Confirmation</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
                        <span>Emergency Roadside Assistance</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </InView>

          {/* Contact Form - Right Side */}
          <InView triggerOnce={true} threshold={0.3}>
            {({ inView, ref }) => (
              <div
                ref={ref}
                className={`lg:col-span-2 ${inView ? 'animate__animated animate__fadeInRight' : ''}`}
              >
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl">
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-white mb-2">Send Us a Message</h3>
                    <p className="text-blue-200">Fill out the form below and we'll get back to you shortly</p>
                  </div>

                  {/* Messages */}
                  {successMessage && (
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4 mb-6 rounded-xl shadow-lg transform transition-all duration-500">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">✅</span>
                        <span className="font-semibold">{successMessage}</span>
                      </div>
                    </div>
                  )}
                  {errorMessage && (
                    <div className="bg-gradient-to-r from-red-500 to-pink-600 text-white p-4 mb-6 rounded-xl shadow-lg transform transition-all duration-500">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">❌</span>
                        <span className="font-semibold">{errorMessage}</span>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Name Field */}
                      <div className="group">
                        <label htmlFor="name" className="block text-white font-semibold mb-3 text-sm uppercase tracking-wide">
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full p-4 bg-white/5 border border-white/20 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:border-cyan-400 focus:bg-white/10 transition-all duration-300"
                          placeholder="Enter your full name"
                          required
                        />
                      </div>

                      {/* Email Field */}
                      <div className="group">
                        <label htmlFor="email" className="block text-white font-semibold mb-3 text-sm uppercase tracking-wide">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full p-4 bg-white/5 border border-white/20 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:border-cyan-400 focus:bg-white/10 transition-all duration-300"
                          placeholder="Enter your email address"
                          required
                        />
                      </div>
                    </div>

                    {/* Message Field */}
                    <div className="group">
                      <label htmlFor="message" className="block text-white font-semibold mb-3 text-sm uppercase tracking-wide">
                        Your Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full p-4 bg-white/5 border border-white/20 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:border-cyan-400 focus:bg-white/10 transition-all duration-300 resize-none"
                        rows="5"
                        placeholder="Tell us about your requirements..."
                        required
                      ></textarea>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center space-x-3">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Sending Your Message...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-3">
                          <span className="text-lg">🚀</span>
                          <span>Send Message</span>
                        </div>
                      )}
                    </button>
                  </form>

                  {/* Additional Info */}
                  <div className="mt-6 text-center">
                    <p className="text-blue-200 text-sm">
                      We typically respond within 2-4 hours during business hours
                    </p>
                  </div>
                </div>
              </div>
            )}
          </InView>
        </div>

        {/* Quick Stats */}
        <InView triggerOnce={true} threshold={0.3}>
          {({ inView, ref }) => (
            <div ref={ref} className={`mt-16 ${inView ? 'animate__animated animate__fadeInUp' : ''}`}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { number: "24/7", label: "Support Available" },
                  { number: "2h", label: "Avg Response Time" },
                  { number: "500+", label: "Happy Customers" },
                  { number: "50+", label: "Cities Covered" }
                ].map((stat, index) => (
                  <div key={index} className="text-center bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
                    <div className="text-2xl font-bold text-cyan-400 mb-2">{stat.number}</div>
                    <div className="text-blue-200 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </InView>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default ContactUs;